/* Note export utilities — PDF, Word (.doc), Excel (.xlsx). */
import html2pdf from "html2pdf.js";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/** Convert a CSS paper-format to print size in mm. */
export const PAPER_SIZES = {
    A3: { w: 297, h: 420, label: "A3" },
    A4: { w: 210, h: 297, label: "A4" },
    A5: { w: 148, h: 210, label: "A5" },
    letter: { w: 215.9, h: 279.4, label: "Letter" },
    free: { w: 210, h: 297, label: "Free" },
};

/** Build a safe filename (alphanumerics + dash + underscore + dot). */
function safeName(name, ext) {
    const base = (name || "Notiz").replace(/[^a-zA-Z0-9_\- ]+/g, "").trim() || "Notiz";
    return `${base}.${ext}`;
}

/* ------------- PDF export ------------- */
export async function exportNoteToPDF({ title, html, paper_format = "A4", paper_orientation = "portrait", letterhead_url = null }) {
    const size = PAPER_SIZES[paper_format] || PAPER_SIZES.A4;
    const orientation = paper_orientation === "landscape" ? "landscape" : "portrait";
    const W = orientation === "landscape" ? size.h : size.w;
    const H = orientation === "landscape" ? size.w : size.h;

    // If a letterhead background is set, embed it as the first layer with
    // absolute positioning so it covers the entire page underneath the content.
    const letterheadCSS = letterhead_url ? `
        <div style="position:absolute; inset:0; z-index:0;
                    background-image:url('${letterhead_url}');
                    background-size:100% 100%;
                    background-repeat:no-repeat;"></div>` : "";

    const wrap = document.createElement("div");
    wrap.style.position = "relative";
    wrap.style.padding = "24px";
    wrap.style.fontFamily = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
    wrap.style.color = "#131022";
    wrap.style.background = "#ffffff";
    wrap.style.width = `${W}mm`;
    wrap.style.minHeight = `${H}mm`;
    wrap.innerHTML = `
        ${letterheadCSS}
        <div style="position:relative; z-index:1;">
            <h1 style="font-family:'DM Serif Display', Georgia, serif; font-size:24pt; margin:0 0 16px 0;">${escapeHtml(title || "Notiz")}</h1>
            <div class="note-content" style="font-size:11pt; line-height:1.55;">${html || ""}</div>
        </div>
    `;
    // Style tables inline for fidelity
    wrap.querySelectorAll("table").forEach((tbl) => {
        tbl.style.borderCollapse = "collapse";
        tbl.style.width = "100%";
        tbl.style.margin = "8px 0";
        tbl.querySelectorAll("th,td").forEach((cell) => {
            cell.style.border = "1px solid #94a3b8";
            cell.style.padding = "6px 8px";
            cell.style.verticalAlign = "top";
        });
        tbl.querySelectorAll("th").forEach((th) => {
            th.style.background = "#f1f5f9";
            th.style.fontWeight = "600";
        });
    });
    const opts = {
        margin: 12,
        filename: safeName(title, "pdf"),
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
        jsPDF: { unit: "mm", format: [W, H], orientation },
    };
    await html2pdf().from(wrap).set(opts).save();
}

/* ------------- Word (.doc) export -------------
 * Microsoft Word reliably opens HTML wrapped with the MS Office namespace
 * declarations and saved with a .doc extension.  This produces a fully editable
 * Word document that preserves headings, lists, tables, bold/italic, alignment. */
export function exportNoteToDOC({ title, html, paper_format = "A4", paper_orientation = "portrait", letterhead_url = null }) {
    const size = PAPER_SIZES[paper_format] || PAPER_SIZES.A4;
    const orientation = paper_orientation === "landscape" ? "landscape" : "portrait";
    const W = orientation === "landscape" ? size.h : size.w;
    const H = orientation === "landscape" ? size.w : size.h;
    const pageCSS = `@page WordSection1 { size: ${W}mm ${H}mm; margin: 20mm 20mm; }`;
    const lhStyle = letterhead_url ? `body { background-image: url('${letterhead_url}'); background-size: 100% 100%; background-repeat: no-repeat; }` : "";
    const doc = `
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title || "Notiz")}</title>
<style>
${pageCSS}
${lhStyle}
div.WordSection1 { page: WordSection1; }
body { font-family: 'Calibri', 'Arial', sans-serif; font-size: 11pt; color: #131022; }
h1 { font-family: 'Georgia', serif; font-size: 22pt; margin: 0 0 12pt 0; }
h2 { font-size: 16pt; margin: 12pt 0 6pt 0; }
h3 { font-size: 13pt; margin: 10pt 0 6pt 0; }
table { border-collapse: collapse; margin: 6pt 0; }
th, td { border: 1pt solid #94a3b8; padding: 4pt 6pt; vertical-align: top; }
th { background: #f1f5f9; font-weight: 600; }
ul, ol { margin: 6pt 0 6pt 24pt; }
blockquote { border-left: 3pt solid #cbd5e1; padding-left: 10pt; color: #475569; margin: 6pt 0; }
</style>
</head>
<body>
<div class="WordSection1">
<h1>${escapeHtml(title || "Notiz")}</h1>
${html || ""}
</div>
</body>
</html>`.trim();
    const blob = new Blob(["\ufeff", doc], { type: "application/msword;charset=utf-8" });
    saveAs(blob, safeName(title, "doc"));
}

/* ------------- Excel (.xlsx) export -------------
 * Strategy: every <table> in the rich content becomes its own worksheet.
 * If there are no tables, we put the plain text into a single sheet. */
export function exportNoteToXLSX({ title, html }) {
    const container = document.createElement("div");
    container.innerHTML = html || "";
    const tables = container.querySelectorAll("table");
    const wb = XLSX.utils.book_new();
    if (tables.length === 0) {
        const text = (container.innerText || "").split("\n").map((line) => [line]);
        const ws = XLSX.utils.aoa_to_sheet(text.length ? text : [[""]]);
        XLSX.utils.book_append_sheet(wb, ws, "Notiz");
    } else {
        tables.forEach((tbl, i) => {
            const ws = XLSX.utils.table_to_sheet(tbl);
            XLSX.utils.book_append_sheet(wb, ws, `Tabelle ${i + 1}`.slice(0, 31));
        });
    }
    XLSX.writeFile(wb, safeName(title, "xlsx"));
}

function escapeHtml(s) {
    return String(s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}
